import ApexCharts from 'apexcharts';

const DARK_THEME = {mode: 'dark'};
const CHART_HEIGHT = 350;

const COLORS = [
    '#ef4444', // red-500
    '#f97316', // orange-500
    '#f59e0b', // amber-500
    '#eab308', // yellow-500
    '#84cc16', // lime-500
    '#22c55e', // green-500
    '#10b981', // emerald-500
    '#14b8a6', // teal-500
    '#06b6d4', // cyan-500
    '#0ea5e9', // sky-500
    '#3b82f6', // blue-500
    '#6366f1', // indigo-500
    '#8b5cf6', // violet-500
    '#a855f7', // purple-500
    '#d946ef', // fuchsia-500
    '#ec4899', // pink-500
];

/**
 * Creates common chart options with modern styling
 */
function getBaseChartOptions(type, height = CHART_HEIGHT) {
    return {
        theme: DARK_THEME,
        chart: {
            type,
            height,
            redrawOnParentResize: true,
            background: 'transparent',
            toolbar: {
                show: true,
                offsetY: -5,
                tools: {
                    download: true,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                    reset: true
                }
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 600,
                animateGradually: {
                    enabled: true,
                    delay: 150
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 350
                }
            }
        },
        colors: COLORS,
        grid: {
            borderColor: '#2c2c38',
            strokeDashArray: 4,
            padding: {
                top: 10,
                right: 20,
                bottom: 10,
                left: 10
            },
            xaxis: {
                lines: {show: true}
            },
            yaxis: {
                lines: {show: true}
            }
        },
        states: {
            hover: {
                filter: {
                    type: 'lighten',
                    value: 0.15
                }
            },
            active: {
                filter: {
                    type: 'darken',
                    value: 0.5
                }
            }
        }
    };
}

/**
 * Renders user score line chart
 */
function userScore(data) {
    const seriesData = [{
        name: 'Points',
        data: data.map(entry => ({
            x: new Date(entry.datetime).getTime(),
            y: entry.score,
            points: entry.points,
            name: entry.name
        }))
    }];

    const options = {
        ...getBaseChartOptions('line'),
        chart: {
            ...getBaseChartOptions('line').chart,
            stacked: false,
            zoom: {enabled: true, type: 'x', autoScaleYaxis: true}
        },
        dataLabels: {enabled: false},
        markers: {
            size: 0,
            hover: {
                size: 7,
                sizeOffset: 3
            }
        },
        stroke: {
            width: 2,
            curve: 'smooth',
            lineCap: 'round'
        },
        xaxis: {
            type: 'datetime',
            labels: {
                datetimeUTC: false,
                datetimeFormatter: {
                    year: 'yyyy',
                    month: 'MMM \'yy',
                    day: 'dd MMM',
                    hour: 'HH:mm'
                },
                rotate: -45,
                style: {
                    colors: '#9ca3af',
                    fontSize: '12px',
                    fontWeight: 500
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            },
            axisTicks: {
                show: true,
                color: '#4b5563'
            },
            tooltip: {enabled: false}
        },
        yaxis: {
            title: {
                text: 'Points',
                style: {
                    color: '#d1d5db',
                    fontSize: '13px',
                    fontWeight: 600
                }
            },
            labels: {
                formatter: value => value.toFixed(0),
                style: {
                    colors: '#9ca3af',
                    fontSize: '12px',
                    fontWeight: 500
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            }
        },
        tooltip: {
            theme: 'dark',
            x: {format: 'HH:mm dd/MM/yyyy'},
            style: {fontSize: '12px'},
            custom: ({seriesIndex, dataPointIndex, w}) => {
                const point = seriesData[seriesIndex].data[dataPointIndex];
                const color = w.config.colors[seriesIndex];
                const date = new Date(point.x).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });

                return `
                    <div class="px-3 py-2 bg-gray-900 border border-gray-700 rounded-md"
                         style="border-left: 3px solid ${color}">
                        <div class="text-xs text-gray-400 mb-1">${date}</div>
                        <div class="flex items-center gap-2 mb-1">
                            <div class="w-2 h-2 rounded-full" style="background:${color}"></div>
                            <span class="font-medium text-white text-sm">${point.name}</span>
                        </div>
                        <div class="flex items-center gap-2 mb-1">
                            <i class="fa-solid fa-plus text-green-400 text-xs"></i>
                            <span class="text-sm font-semibold text-green-400">
                                ${point.points} pts
                            </span>
                        </div>
                        <div class="flex items-center gap-2">
                            <i class="fa-solid fa-star text-yellow-400 text-xs"></i>
                            <span class="text-sm font-semibold text-gray-200">
                                ${point.y} pts total
                            </span>
                        </div>
                    </div>
                `;
            }
        },
        fill: {
            type: 'gradient',
            gradient: {
                shade: 'dark',
                type: 'vertical',
                shadeIntensity: 0.3,
                opacityFrom: 0.8,
                opacityTo: 0.5,
                stops: [0, 90, 100]
            }
        },
        legend: {
            position: 'top',
            horizontalAlign: 'left',
            labels: {colors: '#d1d5db'},
            markers: {
                width: 14,
                height: 14,
                radius: 4
            },
            fontSize: '13px',
            fontWeight: 600
        },
        series: seriesData
    };

    renderChart("#user-score", options);
}

/**
 * Renders user categories radar chart
 */
function userCategories(data) {
    const options = {
        ...getBaseChartOptions('radar'),
        yaxis: {
            show: false,
            labels: {
                style: {
                    colors: '#9ca3af',
                    fontSize: '12px'
                }
            }
        },
        markers: {
            size: 4,
            hover: {size: 8}
        },
        stroke: {
            width: 2,
            colors: COLORS
        },
        fill: {
            opacity: 0.2
        },
        plotOptions: {
            radar: {
                polygons: {
                    strokeColors: '#4b5563',
                    strokeWidth: 2,
                    connectorColors: '#4b5563',
                    fill: {
                        colors: ['#1f1f2e', '#2a2a3a']
                    }
                }
            }
        },
        xaxis: {
            categories: Object.keys(data),
            labels: {
                style: {
                    colors: '#d1d5db',
                    fontSize: '13px',
                    fontWeight: 600
                }
            }
        },
        tooltip: {
            theme: 'dark',
            style: {fontSize: '12px'},
            y: {
                formatter: value => `${value} flags`
            }
        },
        legend: {
            position: 'top',
            horizontalAlign: 'left',
            labels: {colors: '#d1d5db'},
            markers: {
                width: 14,
                height: 14,
                radius: 4
            },
            fontSize: '13px',
            fontWeight: 600
        },
        series: [{name: 'Flags', data: Object.values(data)}]
    };

    renderChart("#user-categories", options);
}

/**
 * Renders user difficulty bar chart
 */
function userDifficulty(data) {
    const options = {
        ...getBaseChartOptions('bar'),
        series: [{name: 'Flags', data: Object.values(data)}],
        plotOptions: {
            bar: {
                borderRadius: 6,
                horizontal: true,
                distributed: true,
                dataLabels: {
                    position: 'top'
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: Object.keys(data),
            labels: {
                style: {
                    colors: '#9ca3af',
                    fontSize: '12px',
                    fontWeight: 500
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#d1d5db',
                    fontSize: '13px',
                    fontWeight: 600
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            }
        },
        tooltip: {
            theme: 'dark',
            style: {fontSize: '12px'},
            y: {
                formatter: value => `${value} flags`,
                title: {
                    formatter: () => ''
                }
            }
        },
        legend: {
            show: false
        }
    };

    renderChart("#user-challenges-difficulty", options);
}

/**
 * Renders user adventure bar chart
 */
function userAdventure(data) {
    const mapNames = Object.keys(data);
    const percentages = Object.values(data).map(entry => entry.percent);
    const values = Object.values(data).map(entry => entry.value);
    const maxValues = Object.values(data).map(entry => entry.max);

    const options = {
        ...getBaseChartOptions('bar'),
        series: [{
            name: 'Progression',
            data: percentages
        }],
        plotOptions: {
            bar: {
                borderRadius: 8,
                horizontal: false,
                columnWidth: '60%',
                distributed: true,
                dataLabels: {
                    position: 'top'
                }
            }
        },
        dataLabels: {
            enabled: true,
            formatter: val => `${val}%`,
            offsetY: -20,
            style: {
                fontSize: '12px',
                fontWeight: 600,
                colors: ['#d1d5db']
            }
        },
        xaxis: {
            categories: mapNames,
            labels: {
                style: {
                    colors: '#d1d5db',
                    fontSize: '13px',
                    fontWeight: 600
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            }
        },
        yaxis: {
            title: {
                text: 'Progression (%)',
                style: {
                    color: '#d1d5db',
                    fontSize: '13px',
                    fontWeight: 600
                }
            },
            max: 100,
            labels: {
                formatter: value => `${value}%`,
                style: {
                    colors: '#9ca3af',
                    fontSize: '12px',
                    fontWeight: 500
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            }
        },
        tooltip: {
            theme: 'dark',
            style: {fontSize: '12px'},
            custom: ({seriesIndex, dataPointIndex, w}) => {
                const mapName = mapNames[dataPointIndex];
                const percent = percentages[dataPointIndex];
                const value = values[dataPointIndex];
                const max = maxValues[dataPointIndex];
                const color = w.config.colors[dataPointIndex];

                return `
                    <div class="px-3 py-2 bg-gray-900 border border-gray-700 rounded-md"
                         style="border-left: 3px solid ${color}">
                        <div class="font-semibold text-white mb-2">${mapName}</div>
                        <div class="flex items-center gap-2 mb-1">
                            <i class="fa-solid fa-flag text-yellow-400 text-xs"></i>
                            <span class="text-sm text-gray-300">${value} / ${max} flags</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <i class="fa-solid fa-chart-line text-green-400 text-xs"></i>
                            <span class="text-sm font-semibold text-green-400">${percent}%</span>
                        </div>
                    </div>
                `;
            }
        },
        legend: {
            show: false
        }
    };

    renderChart("#user-adventure", options);
}

/**
 * Renders user activity area chart
 */
function userActivity(data) {
    if (!data || data.length === 0) return;

    // Transform data for area chart
    const seriesData = data.map(entry => ({
        x: new Date(entry.date).getTime(),
        y: entry.count
    }));

    const options = {
        ...getBaseChartOptions('area'),
        chart: {
            ...getBaseChartOptions('area').chart,
            stacked: false,
            zoom: {enabled: true, type: 'x', autoScaleYaxis: true}
        },
        series: [{
            name: 'Flags validés',
            data: seriesData
        }],
        dataLabels: {
            enabled: false
        },
        stroke: {
            width: 3,
            curve: 'smooth',
            lineCap: 'round'
        },
        markers: {
            size: 0,
            hover: {
                size: 6,
                sizeOffset: 3
            }
        },
        fill: {
            type: 'gradient',
            gradient: {
                shade: 'dark',
                type: 'vertical',
                shadeIntensity: 0.5,
                gradientToColors: ['#ef4444'],
                inverseColors: false,
                opacityFrom: 0.8,
                opacityTo: 0.1,
                stops: [0, 90, 100]
            }
        },
        xaxis: {
            type: 'datetime',
            labels: {
                datetimeUTC: false,
                datetimeFormatter: {
                    year: 'yyyy',
                    month: 'MMM \'yy',
                    day: 'dd MMM',
                    hour: 'HH:mm'
                },
                rotate: -45,
                style: {
                    colors: '#9ca3af',
                    fontSize: '12px',
                    fontWeight: 500
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            },
            axisTicks: {
                show: true,
                color: '#4b5563'
            }
        },
        yaxis: {
            title: {
                text: 'Flags validés',
                style: {
                    color: '#d1d5db',
                    fontSize: '13px',
                    fontWeight: 600
                }
            },
            labels: {
                formatter: value => Math.round(value),
                style: {
                    colors: '#9ca3af',
                    fontSize: '12px',
                    fontWeight: 500
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            }
        },
        tooltip: {
            theme: 'dark',
            style: {fontSize: '12px'},
            x: {format: 'dd/MM/yyyy'},
            y: {
                formatter: value => `${value} flag${value > 1 ? 's' : ''}`
            }
        },
        legend: {
            show: false
        }
    };

    renderChart("#user-activity", options);
}

/**
 * Renders points distribution donut chart
 */
function userPointsDistribution(data) {
    if (!data || Object.keys(data).length === 0) return;

    const options = {
        ...getBaseChartOptions('donut', 380),
        series: Object.values(data),
        labels: Object.keys(data),
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#d1d5db'
                        },
                        value: {
                            show: true,
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#ef4444',
                            formatter: val => `${val} pts`
                        },
                        total: {
                            show: true,
                            label: 'Total',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#9ca3af',
                            formatter: w => {
                                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                                return `${total} pts`;
                            }
                        }
                    }
                }
            }
        },
        dataLabels: {
            enabled: true,
            style: {
                fontSize: '12px',
                fontWeight: 600
            },
            dropShadow: {
                enabled: false
            }
        },
        tooltip: {
            theme: 'dark',
            style: {fontSize: '12px'},
            y: {
                formatter: value => `${value} points`
            }
        },
        legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            labels: {colors: '#d1d5db'},
            markers: {
                width: 14,
                height: 14,
                radius: 4
            },
            fontSize: '13px',
            fontWeight: 600
        }
    };

    renderChart("#user-points-distribution", options);
}

/**
 * Renders monthly progress chart with dual axes
 */
function userMonthlyProgress(data) {
    if (!data || data.length === 0) return;

    const options = {
        ...getBaseChartOptions('line'),
        chart: {
            ...getBaseChartOptions('line').chart,
            type: 'line',
            stacked: false
        },
        series: [
            {
                name: 'Flags',
                type: 'column',
                data: data.map(entry => entry.flags)
            },
            {
                name: 'Points',
                type: 'line',
                data: data.map(entry => entry.points)
            }
        ],
        stroke: {
            width: [0, 3],
            curve: 'smooth'
        },
        plotOptions: {
            bar: {
                borderRadius: 6,
                columnWidth: '50%'
            }
        },
        fill: {
            opacity: [0.85, 1],
            gradient: {
                inverseColors: false,
                shade: 'light',
                type: 'vertical',
                opacityFrom: 0.85,
                opacityTo: 0.55,
                stops: [0, 100, 100, 100]
            }
        },
        labels: data.map(entry => entry.month),
        markers: {
            size: [0, 5],
            hover: {
                size: [0, 8]
            }
        },
        xaxis: {
            labels: {
                style: {
                    colors: '#9ca3af',
                    fontSize: '12px',
                    fontWeight: 500
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            }
        },
        yaxis: [
            {
                title: {
                    text: 'Flags',
                    style: {
                        color: '#d1d5db',
                        fontSize: '13px',
                        fontWeight: 600
                    }
                },
                labels: {
                    formatter: value => Math.round(value),
                    style: {
                        colors: '#9ca3af',
                        fontSize: '12px',
                        fontWeight: 500
                    }
                },
                axisBorder: {
                    show: true,
                    color: '#4b5563'
                }
            },
            {
                opposite: true,
                title: {
                    text: 'Points',
                    style: {
                        color: '#d1d5db',
                        fontSize: '13px',
                        fontWeight: 600
                    }
                },
                labels: {
                    formatter: value => Math.round(value),
                    style: {
                        colors: '#9ca3af',
                        fontSize: '12px',
                        fontWeight: 500
                    }
                },
                axisBorder: {
                    show: true,
                    color: '#4b5563'
                }
            }
        ],
        tooltip: {
            theme: 'dark',
            style: {fontSize: '12px'},
            shared: true,
            intersect: false,
            y: [
                {
                    formatter: value => `${value} flag${value > 1 ? 's' : ''}`
                },
                {
                    formatter: value => `${value} points`
                }
            ]
        },
        legend: {
            position: 'top',
            horizontalAlign: 'left',
            labels: {colors: '#d1d5db'},
            markers: {
                width: 14,
                height: 14,
                radius: 4
            },
            fontSize: '13px',
            fontWeight: 600
        }
    };

    renderChart("#user-monthly-progress", options);
}

/**
 * Helper to render a chart in a selector
 */
function renderChart(selector, options) {
    const element = document.querySelector(selector);
    if (element) {
        new ApexCharts(element, options).render();
    }
}

/**
 * Initializes all user progress charts
 */
export function init() {
    const scoreDataElement = document.getElementById('user_stats');
    if (!scoreDataElement) return;

    const chartData = getChartData('user_stats');

    userScore(chartData.score);
    userCategories(chartData.categories);
    userDifficulty(chartData.difficulties);
    userAdventure(chartData.adventure);

    if (chartData.activity) userActivity(chartData.activity);
    if (chartData.points_distribution) userPointsDistribution(chartData.points_distribution);
    if (chartData.monthly_progress) userMonthlyProgress(chartData.monthly_progress);

    // Trigger resize to ensure charts render correctly
    setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
}

/**
 * Retrieves and parses chart data from DOM element
 */
function getChartData(elementId) {
    const element = document.getElementById(elementId);
    return element ? JSON.parse(element.textContent) : null;
}
